import {
  TableContainer,
  Table,
  TableRow,
  styled,
  TableCell,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import {
  BackupPolicyType,
  ScheduleMode,
  type BackupVault,
  type BackupPolicyConfig,
  Provider,
} from '@repo/api-gw-sdk';
import React from 'react';

import { AccountAsVaultTag, VaultTag } from '@/components/vaults/vaultTag';
import { ResourceTypes } from '@/data/inventory/resourceTypes';
import { fromDaysToBiggestAsPossible } from '@/utils/dateTime';

import {
  deconstructCron,
  getFrequencyByWindow,
  getFrequencyDisplayName,
  getHoursDisplay,
  getWindowDurationDisplay,
} from '../frequency';

const PolicyTableCell = styled(TableCell)(() => ({
  borderBottom: 'none',
  padding: 0,
  verticalAlign: 'top',
  paddingRight: '40px',

  '&:last-child': {
    paddingRight: 0,
  },
}));

const PolicyTableHeaderCell = styled(TableCell)(() => ({
  border: 'none',
  padding: 0,
  paddingBottom: '24px',
  paddingRight: '40px',

  '&:last-child': {
    paddingRight: 0,
  },
}));

interface BackupPolicyRetentionSchedulesTableProps {
  policy: BackupPolicyConfig;
  vaults?: BackupVault[];
}

const BackupPolicySchedulesTableContainer = (props: {
  children: React.ReactNode;
}) => {
  return (
    <TableContainer className='mb-[12px]'>
      <Table>
        <BackupPolicySchedulesTableHeader />
        <tbody>{props.children}</tbody>
      </Table>
    </TableContainer>
  );
};

export function BackupPolicySchedulesTable(
  props: BackupPolicyRetentionSchedulesTableProps
) {
  const isNativeStandard =
    props.policy.policyType ===
    BackupPolicyType.BACKUP_POLICY_TYPE_NATIVE_STANDARD;
  const resourceType = props.policy.resourceTypes?.[0];
  const sourceCloudProvider = resourceType
    ? ResourceTypes[resourceType].provider
    : Provider.Aws;

  return (
    <Stack>
      {props.policy.scheduleMode === ScheduleMode.SCHEDULE_MODE_ADVANCED && (
        <Typography className='mb-[36px] mt-[12px]'>
          {props.policy.schedules[0]?.useUtc
            ? `Backup schedules are in UTC.`
            : 'Backup schedules follow the resource time zones.'}
        </Typography>
      )}
      <BackupPolicySchedulesTableContainer>
        {props.policy.schedules.map((schedule, index, items) => {
          const vault = props.vaults?.find((v) => v.id === schedule.vaultId);

          const { hours, minutes } = deconstructCron(schedule.windows);
          const frequencyDisplayName = getFrequencyDisplayName(
            schedule.windows
          );

          return (
            <React.Fragment key={schedule.id}>
              <TableRow>
                <PolicyTableCell width='272px'>
                  <Stack direction='row' gap='12px'>
                    <Typography width='80px' minWidth='80px' maxWidth='80px'>
                      {getFrequencyByWindow(schedule.windows)?.text}
                    </Typography>
                    {props.policy.scheduleMode ===
                      ScheduleMode.SCHEDULE_MODE_ADVANCED && (
                      <Stack gap='12px' sx={{ maxWidth: '140px' }}>
                        {frequencyDisplayName && (
                          <Typography>{frequencyDisplayName}</Typography>
                        )}
                        <Typography variant='subtitle2'>
                          {getHoursDisplay(hours, minutes)}
                          <br />
                          {`Starts within ${getWindowDurationDisplay(schedule.duration / 60)}`}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </PolicyTableCell>
                <PolicyTableCell width='140px'>
                  <RetentionCell retentionDays={schedule.backupRetention} />
                </PolicyTableCell>
                <PolicyTableCell>
                  {isNativeStandard ? (
                    <AccountAsVaultTag
                      cloudProvider={sourceCloudProvider}
                      region={schedule.region || 'Source region'}
                    />
                  ) : (
                    vault && <VaultTag vault={vault} />
                  )}
                </PolicyTableCell>
              </TableRow>
              {index !== items.length - 1 && (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    sx={{ border: 'none', padding: '24px 0' }}
                  >
                    <Divider sx={{ borderStyle: 'dashed' }} />
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          );
        })}
      </BackupPolicySchedulesTableContainer>
    </Stack>
  );
}

export const BackupPolicyContinuousSchedulesTable = ({
  policy,
}: {
  policy: BackupPolicyConfig;
}) => {
  const resourceType = policy.resourceTypes?.[0];
  const cloudProvider = resourceType
    ? ResourceTypes[resourceType].provider
    : Provider.ProviderUnspecified;
  const isBigQueryPitr = !!policy.bigQueryNativePitrConfig;
  const retentionDays =
    policy.bigQueryNativePitrConfig?.retentionDays ??
    policy.awsNativePitrConfig?.retentionDays ??
    0;
  return (
    <BackupPolicySchedulesTableContainer>
      <TableRow>
        <PolicyTableCell width='272px'>
          <Typography>Continuous</Typography>
        </PolicyTableCell>
        <PolicyTableCell width='140px'>
          <Typography>
            {isBigQueryPitr
              ? 'Based on time travel window'
              : `${retentionDays} days`}
          </Typography>
        </PolicyTableCell>
        <PolicyTableCell>
          <AccountAsVaultTag
            cloudProvider={cloudProvider}
            region='Source-region'
          />
        </PolicyTableCell>
      </TableRow>
    </BackupPolicySchedulesTableContainer>
  );
};

const RetentionCell = ({ retentionDays }: { retentionDays: number }) => {
  const formattedRetention = fromDaysToBiggestAsPossible(retentionDays);
  return (
    <Stack gap='4px'>
      <Typography>{`${formattedRetention.value} ${formattedRetention.unit}`}</Typography>
      {formattedRetention.unit !== 'days' && (
        <Typography>{`(${retentionDays} days)`}</Typography>
      )}
    </Stack>
  );
};

const BackupPolicySchedulesTableHeader = () => {
  return (
    <thead>
      <TableRow>
        <PolicyTableHeaderCell width='272px'>
          <Typography variant='h4'>Frequency</Typography>
        </PolicyTableHeaderCell>
        <PolicyTableHeaderCell width='140px'>
          <Typography variant='h4'>Retention</Typography>
        </PolicyTableHeaderCell>
        <PolicyTableHeaderCell>
          <Typography variant='h4'>Destination</Typography>
        </PolicyTableHeaderCell>
      </TableRow>
    </thead>
  );
};
